import { Button, Heading, Input, Stack, Select, MultiSelect } from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import { deduplicateFirstByKey } from '@sixfold/typed-primitives';
import React from 'react';
import { useNavigate } from 'react-router';

import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import { CompanyAgreementStatus, CompanyTypeInput, UnifiedCompanySignupStatus } from '../../lib/graphql';
import { Routes } from '../../routes';
import { getSignupStatusName } from '../utils';

const AGREEMENT_STATUS_OPTIONS: { label: string; value: CompanyAgreementStatus }[] = [
  { label: 'DPA (PUA v14+)', value: CompanyAgreementStatus.HAS_DPA },
  { label: 'No DPA (pre-v14)', value: CompanyAgreementStatus.NO_DPA },
  { label: 'Light registration', value: CompanyAgreementStatus.LIGHT_REGISTRATION },
];

// Synthetic dropdown value that represents "Visibility source pending — reverted".
// Lives only in the URL (`signup_status` param) and the dropdown; the container
// translates it into the real GraphQL variables (NO_VISIBILITY_SOURCE + showOnlyOnboardingRolledBack=true).
export const SIGNUP_STATUS_REVERTED_VALUE = 'no_visibility_source_reverted';

export type CompanyFilterInput = CompanyTypeInput & {
  text_search?: string;
  tag_name?: string;
  signup_status?: string;
  feature_flags?: string[];
  agreement_status?: string;
};

export interface CompanyFiltersProps {
  activeFilters: {
    textSearch?: string;
    tagName?: string;
    type: {
      is_carrier?: boolean;
      is_shipper?: boolean;
      is_paying_customer?: boolean;
      is_test?: boolean;
      is_pilot?: boolean;
      is_pending_signup?: boolean;
    };
    signupStatus?: string;
    featureFlags?: string[];
    agreementStatuses?: string[];
  };
  onFilterToggle: (filter: { name: keyof CompanyFilterInput; value: string | string[] | undefined }) => void;
  onClearFilters: () => void;
  availableTags: { name: string }[];
  availableFeatureFlags: { feature_flag_id: string; display_name: string }[];
}

export const CompanyFilters: React.FC<CompanyFiltersProps> = (props) => {
  const { textSearch, signupStatus, tagName, featureFlags, agreementStatuses } = props.activeFilters;
  const { activeFilters, onFilterToggle, onClearFilters, availableTags } = props;
  const { is_shipper, is_carrier, is_pilot, is_test, is_paying_customer, is_pending_signup } = activeFilters.type;
  const isAdmin = useIsAdmin();
  const hasCompanyEditPermission = useHasScopes([BackofficeScope.companyWriteCompany]);
  const isPrivilegedUser = isAdmin || hasCompanyEditPermission;
  const tagOptions = availableTags.map((tag) => ({ value: tag.name, label: tag.name }));
  const featureFlagsOptions = deduplicateFirstByKey(
    props.availableFeatureFlags
      .map(({ feature_flag_id, display_name }) => ({
        value: feature_flag_id,
        label: display_name,
        description: feature_flag_id,
      }))
      .concat(
        (featureFlags ?? []).map((featureFlagId) => ({
          value: featureFlagId,
          label: featureFlagId,
          description: featureFlagId,
        })),
      ),
    ({ value }) => value,
  );
  const navigate = useNavigate();
  const hasActiveFilters =
    is_carrier !== undefined ||
    is_shipper !== undefined ||
    is_test !== undefined ||
    is_pilot !== undefined ||
    is_paying_customer !== undefined ||
    textSearch !== undefined ||
    tagName !== undefined ||
    signupStatus !== undefined ||
    featureFlags !== undefined ||
    agreementStatuses !== undefined;

  return (
    <Stack direction="column" gap="small" className="companyFilters">
      <Stack direction="row" alignItems="center">
        <Heading size="large">Companies</Heading>
        {isPrivilegedUser && (
          <Button kind="primary" size="small" onClick={() => void navigate(Routes.CompanyCreate.generatePath({}))}>
            Add
          </Button>
        )}
      </Stack>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr repeat(5, 1fr)', gap: '8px', alignItems: 'end' }}>
        <Input
          label="Company search"
          labelPosition="hidden"
          size="small"
          name="text_search"
          placeholder="Company search..."
          value={textSearch !== undefined ? textSearch : ''}
          onChange={({ value }) => {
            onFilterToggle({ name: 'text_search', value: value === '' ? undefined : value });
          }}
        />
        <Select
          label="Shipper"
          labelPosition="hidden"
          placeholder="Shipper"
          isClearable
          options={[
            { label: 'Is a shipper', value: 'true' },
            { label: 'Not a shipper', value: 'false' },
          ]}
          value={is_shipper === undefined ? null : is_shipper.toString()}
          onChange={(option) => {
            onFilterToggle({ name: 'is_shipper', value: option === null ? undefined : option.value });
          }}
        />
        <Select
          label="Carrier"
          labelPosition="hidden"
          placeholder="Carrier"
          isClearable
          options={[
            { label: 'Is a carrier', value: 'true' },
            { label: 'Not a carrier', value: 'false' },
          ]}
          value={is_carrier === undefined ? null : is_carrier.toString()}
          onChange={(option) => {
            onFilterToggle({ name: 'is_carrier', value: option === null ? undefined : option.value });
          }}
        />
        <Select
          label="Test"
          labelPosition="hidden"
          placeholder="Test"
          isClearable
          options={[
            { label: 'Is test', value: 'true' },
            { label: 'Is not test', value: 'false' },
          ]}
          value={is_test === undefined ? null : is_test.toString()}
          onChange={(option) => {
            onFilterToggle({ name: 'is_test', value: option === null ? undefined : option.value });
          }}
        />
        <Select
          label="Paying"
          labelPosition="hidden"
          placeholder="Paying"
          isClearable
          options={[
            { label: 'Is paying', value: 'true' },
            { label: 'Is not paying', value: 'false' },
          ]}
          value={is_paying_customer === undefined ? null : is_paying_customer.toString()}
          onChange={(option) => {
            onFilterToggle({ name: 'is_paying_customer', value: option === null ? undefined : option.value });
          }}
        />
        <Select
          label="Pilot"
          labelPosition="hidden"
          placeholder="Pilot"
          isClearable
          options={[
            { label: 'Is a pilot', value: 'true' },
            { label: 'Is not a pilot', value: 'false' },
          ]}
          value={is_pilot === undefined ? null : is_pilot.toString()}
          onChange={(option) => {
            onFilterToggle({ name: 'is_pilot', value: option === null ? undefined : option.value });
          }}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr) auto', gap: '8px', alignItems: 'end' }}>
        <Select
          label="Status"
          labelPosition="hidden"
          placeholder="Status"
          isClearable
          options={Object.keys(UnifiedCompanySignupStatus)
            .filter((key) => key !== 'INVITED')
            .flatMap((key) => {
              const value = UnifiedCompanySignupStatus[key].toLowerCase();
              const baseOption = {
                label: getSignupStatusName(UnifiedCompanySignupStatus[key]),
                value,
              };
              if (UnifiedCompanySignupStatus[key] === UnifiedCompanySignupStatus.NO_VISIBILITY_SOURCE) {
                return [
                  baseOption,
                  {
                    label: `${baseOption.label} — reverted`,
                    value: SIGNUP_STATUS_REVERTED_VALUE,
                  },
                ];
              }
              return [baseOption];
            })}
          value={signupStatus ?? null}
          onChange={(option) => {
            onFilterToggle({ name: 'signup_status', value: option === null ? undefined : option.value });
          }}
        />
        <Select
          label="Signup status"
          labelPosition="hidden"
          placeholder="Signup status"
          isClearable
          options={[
            { label: 'Is pending signup', value: 'true' },
            { label: 'Is not pending signup', value: 'false' },
          ]}
          value={is_pending_signup === undefined ? null : is_pending_signup.toString()}
          onChange={(option) => {
            onFilterToggle({ name: 'is_pending_signup', value: option === null ? undefined : option.value });
          }}
        />
        <Select
          label="Tags"
          labelPosition="hidden"
          placeholder="Tags"
          isClearable
          isFilterable
          options={tagOptions}
          value={tagName === undefined ? null : tagName}
          onChange={(option) => {
            onFilterToggle({ name: 'tag_name', value: option === null ? undefined : option.value });
          }}
        />
        <MultiSelect
          label="Enabled feature flags"
          labelPosition="hidden"
          placeholder="Enabled feature flags"
          clearAllLabel="Clear all"
          options={featureFlagsOptions}
          value={(featureFlags ?? []).map(
            (value) =>
              featureFlagsOptions.find((option) => option.value === value) ?? {
                value,
                label: value,
                description: value,
              },
          )}
          onChange={(options) => {
            onFilterToggle({
              name: 'feature_flags',
              value: !options.length ? undefined : options.map(({ value }) => value),
            });
          }}
          filterOption={(option, input) => {
            return !input
              ? true
              : [option.value, option.label, option.description].some((searchable) =>
                  searchable.toLowerCase().includes(input.toLowerCase()),
                );
          }}
        />
        <MultiSelect
          label="Agreement"
          labelPosition="hidden"
          placeholder="Agreement / DPA"
          clearAllLabel="Clear all"
          options={AGREEMENT_STATUS_OPTIONS}
          value={AGREEMENT_STATUS_OPTIONS.filter((option) => (agreementStatuses ?? []).includes(option.value))}
          onChange={(options) => {
            onFilterToggle({
              name: 'agreement_status',
              value: !options.length ? undefined : options.map(({ value }) => value),
            });
          }}
        />
        {hasActiveFilters && <Button onClick={onClearFilters}>Clear</Button>}
      </div>
    </Stack>
  );
};
